import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Chip,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { QuestionField, FieldOption, QuestionnairePopup } from '../../types/questionnaire.types';
import { ValidationEditor } from './ValidationEditor';
import { FieldConditionalLogicEditor } from './FieldConditionalLogicEditor';
import { patientService } from '../../services/patientService';
import patientFieldConfigService, { PatientFieldConfig } from '../../services/patientFieldConfigService';
import leadService, { LeadFieldOption } from '../../services/leadService';
import fileTypeService, { FileType } from '../../services/fileTypeService';
import emailTemplateService, { EmailTemplate, AvailableVariable } from '../../services/emailTemplateService';
import { useTenant } from '../../context/TenantContext';
import { ClinicDialog } from '../dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';
import { resetColorHandler, colorTooltipSx, applyColorTooltips } from '../editor/quillColorReset';

interface FieldEditDialogProps {
  open: boolean;
  field?: QuestionField;
  onSave: (field: QuestionField) => void;
  onCancel: () => void;
  availableFields?: Array<{ name: string; label: string; type: string; options?: FieldOption[] }>;
  availablePopups?: QuestionnairePopup[];
  questionnaireTypeId?: number | null;  // Scopes email-verification template options to this questionnaire's context
  /** The questionnaire's type. A lead mapping is only offered on a
   *  'create_lead' questionnaire - everywhere else it would be a dropdown
   *  that can only ever do nothing. */
  questionnaireKind?: string;
  language?: 'en' | 'he';  // 'he' makes the text typed into the dialog's inputs read right-to-left
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number (Integer)' },
  { value: 'radio', label: 'Radio Button' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'checkboxes', label: 'Checkboxes (Multiple)' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'button-group', label: 'Button Group (Single Select)' },
  { value: 'file', label: 'File Upload' },
  { value: 'stripe_cc', label: 'Credit Card Payment (Stripe)' },
  { value: 'button', label: 'Button' },
  { value: 'content', label: 'Content' },
  { value: 'info-banner', label: 'Info Banner' },
  { value: 'line', label: 'Line (Side by Side)' },
  { value: 'container', label: 'Container (Vertical)' },
];

export const FieldEditDialog: React.FC<FieldEditDialogProps> = ({
  open,
  field,
  onSave,
  onCancel,
  availableFields = [],
  availablePopups = [],
  questionnaireTypeId,
  questionnaireKind,
  language,
}) => {
  const isRTL = language === 'he';
  // Track which field is currently being edited to detect when field changes
  const currentEditIdRef = useRef<string>(`new_${Date.now()}`);
  // Track original field name to determine if it should be cleared when "None" is selected
  const originalFieldNameRef = useRef<string>('');
  // Track previous open state to detect dialog open/close transitions
  const previousOpenRef = useRef<boolean>(false);

  const [editedField, setEditedField] = useState<QuestionField>(
    field || {
      name: '',
      label: '',
      type: 'text',
      validation: undefined,
    }
  );
  const [options, setOptions] = useState(field?.options || []);
  const [optionLabel, setOptionLabel] = useState('');
  const [optionValue, setOptionValue] = useState('');
  const [optionScore, setOptionScore] = useState<string>('');
  const [patientFields, setPatientFields] = useState<Array<{ name: string; label: string; type: string }>>([]);
  const [leadFields, setLeadFields] = useState<LeadFieldOption[]>([]);
  const [patientFieldConfigs, setPatientFieldConfigs] = useState<PatientFieldConfig[]>([]);
  const [loadingPatientFields, setLoadingPatientFields] = useState(false);
  const [usePatientFieldDef, setUsePatientFieldDef] = useState(false);
  const [allowMultiple, setAllowMultiple] = useState(editedField.fileConfig?.allowMultiple || false);
  const [labelInputType, setLabelInputType] = useState<'text' | 'html'>('text');
  const [nameError, setNameError] = useState<string>('');
  const [fileTypes, setFileTypes] = useState<FileType[]>([]);
  const [loadingFileTypes, setLoadingFileTypes] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [selectedFileTypeId, setSelectedFileTypeId] = useState<number | undefined>(
    field?.fileConfig?.fileTypeId
  );

  const { currentTenant } = useTenant();

  // Wrapper around the content rich-text editor, used to label the custom
  // "reset colour" toolbar button after Quill renders it.
  const contentEditorRef = useRef<HTMLDivElement>(null);

  // Content variable picker: create_patient content can use LEAD variables,
  // collect_info content can use PATIENT variables. {name} tokens are inserted
  // into the content editor and substituted in the wizard.
  const contentQuillRef = useRef<ReactQuill>(null);
  const [contentVariables, setContentVariables] = useState<AvailableVariable[]>([]);
  const contentVariableContext =
    questionnaireKind === 'create_patient' ? 'LEAD'
      : questionnaireKind === 'collect_info' ? 'PATIENT'
        : null;

  const insertContentVariable = (name: string) => {
    const editor = contentQuillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, `{${name}}`);
    editor.setSelection(index + name.length + 2, 0);
  };

  useEffect(() => {
    if (open && editedField.type === 'content' && contentVariableContext) {
      emailTemplateService
        .getAvailableVariables(contentVariableContext)
        .then(setContentVariables)
        .catch(() => setContentVariables([]));
    } else {
      setContentVariables([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editedField.type, contentVariableContext]);

  // Toolbar config for the Content rich-text editor, including a custom
  // "reset colour to default" button that strips the applied text colour
  // (falls back to the questionnaire's default colour).
  const contentModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
        ['resetColor'],
      ],
      handlers: {
        resetColor: resetColorHandler,
      },
    },
  }), []);

  // Add tooltips to the colour toolbar controls once Quill has rendered them,
  // so authors understand what each does. Uses a `data-tooltip` attribute (styled
  // via CSS below) instead of the native `title`, so the hint shows immediately
  // rather than after the browser's ~1s delay.
  const contentListStyle = (editedField as any).listStyle;
  useEffect(() => {
    // rAF so the Quill toolbar is in the DOM before we decorate it (dialogs
    // remount their content on open).
    const id = requestAnimationFrame(() => applyColorTooltips(contentEditorRef.current));
    return () => cancelAnimationFrame(id);
  }, [open, editedField.type, contentListStyle]);

  // Load patient fields + dynamic field configs when dialog opens
  useEffect(() => {
    if (open) {
      const fetchPatientFields = async () => {
        try {
          setLoadingPatientFields(true);
          const fields = await patientService.getPatientFields(undefined, currentTenant?.id);
          setPatientFields(fields);
          if (currentTenant) {
            const configs = await patientFieldConfigService.getAll(currentTenant.id);
            setPatientFieldConfigs(configs);
          }
        } catch (error) {
          console.error('Error loading patient fields:', error);
        } finally {
          setLoadingPatientFields(false);
        }
      };
      fetchPatientFields();
      if (questionnaireKind === 'create_lead') {
        leadService.getLeadFields(currentTenant?.id).then(setLeadFields).catch(() => setLeadFields([]));
      }
      setUsePatientFieldDef(field?.usePatientFieldDefinition ?? false);
    }
  }, [open, currentTenant, questionnaireKind, field?.usePatientFieldDefinition]);

  // Load file types when dialog opens - ONLY for file upload fields
  useEffect(() => {
    if (open && currentTenant && (field?.type === 'file' || editedField.type === 'file')) {
      const fetchFileTypes = async () => {
        try {
          setLoadingFileTypes(true);
          const types = await fileTypeService.getPatientFileTypes();
          setFileTypes(types);
        } catch (error) {
          console.error('Error loading file types:', error);
        } finally {
          setLoadingFileTypes(false);
        }
      };
      fetchFileTypes();
    }
  }, [open, currentTenant, field?.type, editedField.type]);

  // Load active email templates scoped to this questionnaire's context (QUESTIONNAIRE_{id})
  // for the verification template picker.
  useEffect(() => {
    if (open && editedField.type === 'email' && questionnaireTypeId) {
      emailTemplateService
        .getTemplates(`QUESTIONNAIRE_${questionnaireTypeId}`, true)
        .then(setEmailTemplates)
        .catch(() => {});
    } else {
      setEmailTemplates([]);
    }
  }, [open, editedField.type, questionnaireTypeId]);

  // Initialize toggle based on existing field content
  useEffect(() => {
    if (open && field && field.type === 'button-group') {
      const htmlContent = (field as any)?.htmlContent;
      const hasHtmlContent = htmlContent && htmlContent.trim() !== '' && htmlContent !== '<p><br></p>' && htmlContent !== '<p></p>';

      setLabelInputType(hasHtmlContent ? 'html' : 'text');
    } else if (open && !field) {
      // New field - default to text
      setLabelInputType('text');
    }
  }, [open, field]);
  // Memoize validation onChange to prevent infinite loop in ValidationEditor
  const handleValidationChange = useCallback((validation: any) => {
    setEditedField((prevField) => ({
      ...prevField,
      validation,
    }));
  }, []);

  // Initialize form when dialog opens or field changes
  // We intentionally depend on field?.name (stable identifier) not the entire field object
  // to prevent unnecessary re-renders when field reference changes
  useEffect(() => {
    if (open) {
      // Generate unique ID for current field (use name or timestamp for new fields)
      const fieldId = field?.name || `new_${Date.now()}`;
      const previousId = currentEditIdRef.current;
      const wasClosedNowOpen = !previousOpenRef.current && open;

      // Reinitialize if: (1) different field, OR (2) dialog was closed and is now opening
      if (fieldId !== previousId || wasClosedNowOpen) {
        currentEditIdRef.current = fieldId;

        if (field) {
          // Store original field name for later comparison
          originalFieldNameRef.current = field.name || '';

          // Clean validation based on field type to remove stale keys
          let cleanedValidation = field.validation ? { ...field.validation } : undefined;
          if (cleanedValidation) {
            if (field.type === 'number') {
              // For number fields, remove string-specific validations
              const { minLength, maxLength, pattern, ...rest } = cleanedValidation;
              cleanedValidation = Object.keys(rest).length > 0 ? rest : undefined;
            } else {
              // For non-number fields, remove number-specific validations
              const { min, max, ...rest } = cleanedValidation;
              cleanedValidation = Object.keys(rest).length > 0 ? rest : undefined;
            }
          }

          const newEditedField: any = {
            ...field,
            validation: cleanedValidation,
          };

          // Ensure button fields have an action property
          if (field.type === 'button' && !field.action) {
            newEditedField.action = 'next';
          }
          setEditedField(newEditedField);
          setOptions(field.options ? [...field.options] : []);

          // Initialize file configuration
          if (field.type === 'file') {
            setAllowMultiple(field.fileConfig?.allowMultiple || false);
            setSelectedFileTypeId(field.fileConfig?.fileTypeId);
          }
        } else {
          // New field - no original name
          originalFieldNameRef.current = '';

          setEditedField({
            name: '',
            label: '',
            type: 'text',
            required: false,
            validation: undefined,
          });
          setOptions([]);

          // Reset file configuration
          setAllowMultiple(false);
          setSelectedFileTypeId(undefined);
        }
        setOptionLabel('');
        setOptionValue('');
      }
    }

    // Update previous open state
    previousOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, field?.name]);

  const hasOptions = ['radio', 'checkboxes', 'select', 'multiselect', 'button-group'].includes(
    editedField.type
  );

  const handleAddOption = () => {
    if (!optionLabel || !optionValue) {
      return;
    }

    const newOption: FieldOption = {
      label: optionLabel,
      value: optionValue
    };

    // Add score if provided
    if (optionScore.trim() !== '') {
      const scoreNum = parseInt(optionScore, 10);
      if (!isNaN(scoreNum)) {
        newOption.score = scoreNum;
      }
    }

    setOptions([...options, newOption]);
    setOptionLabel('');
    setOptionValue('');
    setOptionScore('');
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Field name is always required
    if (!editedField.name.trim()) {
      return;
    }

    // Field name must be unique within the questionnaire
    const trimmedName = editedField.name.trim();
    const originalName = field?.name || '';

    // Field names must not contain "." "[" or "]": react-hook-form treats these as nested
    // object/array paths, so the value is stored under a nested key and the saved answer
    // comes out empty. Block them at edit time.
    if (/[.[\]]/.test(trimmedName)) {
      setNameError('Field name cannot contain the characters ".", "[" or "]"');
      return;
    }
    const isDuplicate = availableFields.some(
      f => f.name === trimmedName && f.name !== originalName
    );
    if (isDuplicate) {
      setNameError(`"${trimmedName}" is already used by another field in this questionnaire`);
      return;
    }

    // Helper to check if HTML content is truly empty
    const hasValidHtmlContent = (html: string | undefined) => {
      if (!html) return false;
      const trimmed = html.trim();
      return trimmed !== '' && trimmed !== '<p><br></p>' && trimmed !== '<p></p>';
    };

    // Label validation: required except for line type and button-group with HTML content
    if (editedField.type !== 'line' && editedField.type !== 'button-group' && (!editedField.label || !editedField.label.trim())) {
      return;
    }

    // For button-group: must have either label or HTML content
    if (editedField.type === 'button-group' && (!editedField.label || !editedField.label.trim()) && !hasValidHtmlContent((editedField as any).htmlContent)) {
      return;
    }

    const fieldToSave: QuestionField = {
      ...editedField,
      // Set default label for line type if empty
      label: editedField.type === 'line' && !editedField.label.trim()
        ? 'Line Container'
        : editedField.label,
      usePatientFieldDefinition: editedField.patientFieldMapping?.startsWith('df_') ? usePatientFieldDef : undefined,
      conditionalLogic: editedField.conditionalLogic,
      ...(hasOptions ? { options } : { options: undefined }),
    };

    if (!hasOptions) {
      delete (fieldToSave as any).options;
    }

    // Email verification config — only persist for email fields with validation enabled
    if (editedField.type !== 'email' || !editedField.validateEmail) {
      delete (fieldToSave as any).validateEmail;
      delete (fieldToSave as any).emailVerificationTemplateId;
    }

    // Handle file configuration
    if (editedField.type === 'file') {
      const selectedFileType = fileTypes.find(t => t.id === selectedFileTypeId);
      fieldToSave.fileConfig = {
        allowMultiple: allowMultiple,
        maxFiles: allowMultiple ? 10 : undefined,  // Hardcoded: 10 files max
        maxFileSize: 10485760,  // Hardcoded: 10MB
        allowedFileTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
        fileTypeId: selectedFileTypeId,  // File type ID
        fileTypeName: selectedFileType?.name,  // File type name (stored for display)
      };
    } else {
      delete (fieldToSave as any).fileConfig;
    }

    onSave(fieldToSave);
  };

  const actions = (
    <>
      <Button onClick={onCancel} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
      <Button
        variant="contained"
        onClick={handleSave}
        sx={CLINIC_DIALOG_SX.primaryButton}
        disabled={
          !editedField.name?.trim() ||
          (editedField.type !== 'line' && editedField.type !== 'button-group' && (!editedField.label || !editedField.label.trim())) ||
          (editedField.type === 'button-group' && (!editedField.label || !editedField.label.trim()) && !((editedField as any).htmlContent?.trim() && (editedField as any).htmlContent !== '<p><br></p>' && (editedField as any).htmlContent !== '<p></p>')) ||
          (editedField.type === 'button' && editedField.action === 'next-and-score' && !editedField.scoreName?.trim()) ||
          (editedField.type === 'info-banner' && (!(editedField as any).scoreTitle?.trim() || !(editedField as any).scoreNameToDisplay?.trim())) ||
          (editedField.type === 'email' && editedField.validateEmail && !editedField.emailVerificationTemplateId)
        }
      >
        Save Field
      </Button>
    </>
  );

  return (
    <ClinicDialog
      open={open}
      onClose={onCancel}
      title={field ? 'Edit Field' : 'Create Field'}
      maxWidth="md"
      actions={actions}
    >
      <Box
        sx={{
          overflowY: 'auto',
          maxHeight: 'calc(85vh - 130px)',
          // In Hebrew mode, make the text entered into inputs and the rich-text
          // (Quill) editors flow right-to-left, without flipping the dialog's
          // overall layout or field labels.
          ...(isRTL && {
            '& input, & textarea, & .ql-editor': { direction: 'rtl', textAlign: 'right' },
          }),
        }}
      >
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          {/* Field Name - Expands when mapping is hidden */}
          <Grid item xs={12} sm={['file', 'button', 'content'].includes(editedField.type) ? 8 : 4}>
            <TextField
              fullWidth
              size="small"
              label="Field Name (Internal)"
              value={editedField.name}
              onChange={(e) => {
                setNameError('');
                setEditedField({ ...editedField, name: e.target.value });
              }}
              variant="outlined"
              placeholder="e.g., full_name"
              error={!!nameError}
              helperText={nameError || undefined}
              InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
            />
          </Grid>

          {/* Mapping to Patient - Show for data fields only (exclude file, button, content, info-banner, line, and container).
              Hidden on a create_lead questionnaire: those answers reach the lead via the Lead mapping below, and the
              patient is populated from the lead at conversion (lead_patient_field_mappings) - a direct patient mapping
              here is never read, so offering it would only mislead. */}
          {questionnaireKind !== 'create_lead'
            && !['file', 'button', 'content', 'info-banner', 'line', 'container'].includes(editedField.type) && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Mapping to Patient (Optional)</InputLabel>
                <Select
                  value={editedField.patientFieldMapping || ''}
                  label="Mapping to Patient (Optional)"
                  sx={CLINIC_DIALOG_SX.select}
                  disabled={loadingPatientFields}
                  onChange={(e) => {
                    const selectedMapping = e.target.value;
                    setUsePatientFieldDef(false);

                    if (selectedMapping) {
                      // If a patient field is selected, update both mapping and field name
                      setEditedField({
                        ...editedField,
                        patientFieldMapping: selectedMapping,
                        name: selectedMapping,
                      });
                    } else {
                      // If "None" is selected
                      const updatedField: any = { ...editedField };
                      delete updatedField.patientFieldMapping;

                      // Case 1: If field was NEW (original name was empty), clear the name
                      // Case 2: If field was existing (had original name), keep the name as is
                      if (originalFieldNameRef.current === '') {
                        updatedField.name = '';
                      }

                      setEditedField(updatedField);
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {patientFields.map((pf) => (
                    <MenuItem key={pf.name} value={pf.name}>
                      {pf.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Mapping to Lead - only on a create_lead questionnaire. Unlike the
              patient mapping this does NOT rename the field: a question can be
              mapped to both sides at once, and the two names would fight. */}
          {questionnaireKind === 'create_lead'
            && !['file', 'button', 'content', 'info-banner', 'line', 'container'].includes(editedField.type) && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Mapping to Lead (Optional)</InputLabel>
                <Select
                  value={editedField.leadFieldMapping || ''}
                  label="Mapping to Lead (Optional)"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) => {
                    const selected = e.target.value;
                    const updated: any = { ...editedField };
                    if (selected) {
                      updated.leadFieldMapping = selected;
                      // A brand-new question has no name yet; give it the
                      // mapping's, so the answer is addressable.
                      if (!updated.name) updated.name = selected;
                    } else {
                      delete updated.leadFieldMapping;
                    }
                    setEditedField(updated);
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {leadFields.map((lf) => (
                    <MenuItem key={lf.name} value={lf.name}>
                      {lf.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Use patient field definition — shown as its own full-width row when a dynamic field is mapped.
              Follows the patient mapping: hidden on create_lead, where the patient mapping itself is not offered. */}
          {questionnaireKind !== 'create_lead' && editedField.patientFieldMapping?.startsWith('df_') && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={usePatientFieldDef}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setUsePatientFieldDef(checked);
                      if (checked) {
                        const key = editedField.patientFieldMapping!.replace(/^df_/, '');
                        const config = patientFieldConfigs.find(c => c.key === key);
                        if (config) {
                          const typeMap: Record<string, string> = {
                            text: 'text',
                            number: 'number',
                            date: 'date',
                            bool: 'checkbox',
                            single_select: 'select',
                          };
                          const mappedType = typeMap[config.field_type] || 'text';
                          const mappedOptions = config.field_type === 'single_select' && config.options
                            ? config.options.map(o => ({ label: o.label, value: o.value }))
                            : undefined;
                          const updated: any = { ...editedField, type: mappedType };
                          if (mappedOptions) updated.options = mappedOptions;
                          else delete updated.options;
                          // Copy patient field validation if questionnaire field has none
                          const hasExistingValidation = editedField.validation &&
                            Object.values(editedField.validation).some(v => v !== undefined && v !== null && v !== '');
                          if (!hasExistingValidation && config.validation) {
                            const pv = config.validation;
                            const qv: Record<string, any> = {};
                            if (config.field_type === 'date' && pv.type) {
                              const dateTypeMap: Record<string, string> = {
                                past_date: 'pastDate',
                                future_date: 'futureDate',
                                within_x_days: 'dateWithinDays',
                                min_age: 'minAge',
                              };
                              qv.validationType = dateTypeMap[pv.type] || pv.type;
                              if (pv.type === 'within_x_days' && pv.value != null) qv.dateWithinDays = pv.value;
                              if (pv.type === 'min_age' && pv.value != null) qv.minAge = pv.value;
                            } else if (config.field_type === 'text') {
                              if (pv.min_length != null) qv.minLength = pv.min_length;
                              if (pv.max_length != null) qv.maxLength = pv.max_length;
                              if (pv.regex) qv.pattern = pv.regex;
                            } else if (config.field_type === 'number') {
                              if (pv.min_value != null) qv.min = pv.min_value;
                              if (pv.max_value != null) qv.max = pv.max_value;
                            }
                            if (pv.error_message) qv.message = pv.error_message;
                            if (Object.keys(qv).length > 0) updated.validation = qv;
                          }
                          setEditedField(updated);
                          if (mappedOptions) setOptions(mappedOptions);
                        }
                      }
                    }}
                  />
                }
                label="Use patient field definition"
              />
            </Grid>
          )}

          {/* Type */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Type</InputLabel>
              <Select
                value={editedField.type}
                label="Type"
                sx={CLINIC_DIALOG_SX.select}
                onChange={(e) => {
                  const newType = e.target.value;
                  const updatedField: any = { ...editedField, type: newType };

                  // Set default action for button type
                  if (newType === 'button') {
                    updatedField.action = 'next';
                  } else {
                    // Remove action if not button type
                    delete updatedField.action;
                  }

                  // Set default values for content type
                  if (newType === 'content') {
                    updatedField.areaType = 'box';
                    updatedField.content = '';
                  } else {
                    // Remove content-specific fields if not content type
                    delete updatedField.areaType;
                    delete updatedField.content;
                  }

                  // Set default values for info-banner type
                  if (newType === 'info-banner') {
                    updatedField.scoreTitle = '';
                    updatedField.scoreNameToDisplay = '';
                  } else {
                    // Remove info-banner-specific fields if not info-banner type
                    delete updatedField.scoreTitle;
                    delete updatedField.scoreNameToDisplay;
                  }

                  // Set default values for line and container types
                  if (newType === 'line' || newType === 'container') {
                    updatedField.nestedFields = updatedField.nestedFields || [];
                  } else {
                    // Remove nested fields if not line or container type
                    delete updatedField.nestedFields;
                  }

                  setEditedField(updatedField);

                  if (!['radio', 'checkbox', 'checkboxes', 'select', 'multiselect'].includes(newType)) {
                    setOptions([]);
                  }
                }}
              >
                {FIELD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Toggle for button-group label type */}
          {editedField.type === 'button-group' && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <ToggleButtonGroup
                  value={labelInputType}
                  exclusive
                  onChange={(e, newType) => {
                    if (newType !== null) {
                      setLabelInputType(newType);
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <ToggleButton value="text">Text</ToggleButton>
                  <ToggleButton value="html">HTML</ToggleButton>
                </ToggleButtonGroup>
                <InputLabel sx={{ fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.87)' }}>
                  Label
                </InputLabel>
              </Box>
            </Grid>
          )}

          {/* Label - Hide for line type and button-group with HTML mode */}
          {editedField.type !== 'line' && !(editedField.type === 'button-group' && labelInputType === 'html') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={editedField.type === 'button-group' ? '' : 'Label (Display)'}
                value={editedField.label}
                onChange={(e) =>
                  setEditedField({ ...editedField, label: e.target.value })
                }
                variant="outlined"
                placeholder={editedField.type === 'container' ? 'Container Title' : 'What is your full name?'}
                InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Grid>
          )}

          {/* HTML Label Content - Show for button-group type with HTML mode */}
          {editedField.type === 'button-group' && labelInputType === 'html' && (
            <Grid item xs={12}>
              <Box>
                <ReactQuill
                  value={(editedField as any).htmlContent || ''}
                  onChange={(content) => {
                    setEditedField({ ...editedField, htmlContent: content } as any);
                  }}
                  theme="snow"
                  placeholder="Add HTML content for label (supports formatting, score placeholders like {SCORE_NAME}, lists, etc.)"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '4px',
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Info for Line Type */}
          {editedField.type === 'line' && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#E3F2FD',
                  borderRadius: 1,
                  border: '1px solid #90CAF9',
                }}
              >
                <span style={{ fontSize: '0.875rem', color: '#1565C0' }}>
                  ℹ️ Line fields display nested fields side by side. Manage nested fields in the section editor by dragging fields into this line or creating new fields within it.
                </span>
              </Box>
            </Grid>
          )}

          {/* Info for Container Type */}
          {editedField.type === 'container' && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#E3F2FD',
                  borderRadius: 1,
                  border: '1px solid #90CAF9',
                }}
              >
                <span style={{ fontSize: '0.875rem', color: '#1565C0' }}>
                  ℹ️ Container fields display nested fields vertically. Manage nested fields in the section editor by dragging fields into this container or creating new fields within it.
                </span>
              </Box>
            </Grid>
          )}

          {/* Area Type - Show for content type */}
          {editedField.type === 'content' && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Area Type</InputLabel>
                <Select
                  value={(editedField as any).areaType || 'box'}
                  label="Area Type"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) => {
                    const newAreaType = e.target.value;
                    const updatedField: any = { ...editedField, areaType: newAreaType };

                    // Initialize summaryData when switching to summary-box
                    if (newAreaType === 'summary-box' && !(editedField as any).summaryData) {
                      updatedField.summaryData = {
                        header: '',
                        rows: [{ label: '', value: '', valueType: 'static' }]
                      };
                    }
                    // Clear summaryData when switching away from summary-box
                    if (newAreaType !== 'summary-box') {
                      delete updatedField.summaryData;
                    }

                    setEditedField(updatedField);
                  }}
                >
                  <MenuItem value="box">Box</MenuItem>
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="filled">Filled</MenuItem>
                  <MenuItem value="summary-box">Summary Box</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* List Style - Show for content type (but not summary-box) */}
          {editedField.type === 'content' && (editedField as any).areaType !== 'summary-box' && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>List Style</InputLabel>
                <Select
                  value={(editedField as any).listStyle || 'default'}
                  label="List Style"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) =>
                    setEditedField({ ...editedField, listStyle: e.target.value } as any)
                  }
                >
                  <MenuItem value="default">Default (Bullets)</MenuItem>
                  <MenuItem value="check">Check Icons</MenuItem>
                  <MenuItem value="dash">Dash Icons</MenuItem>
                  <MenuItem value="custom-icons">Custom Icons</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Custom Icons Configuration - Show when listStyle is 'custom-icons' */}
          {editedField.type === 'content' && (editedField as any).listStyle === 'custom-icons' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Custom Icons"
                value={((editedField as any).customIcons || []).join(', ')}
                onChange={(e) => {
                  const iconsArray = e.target.value
                    .split(',')
                    .map((icon: string) => icon.trim())
                    .filter((icon: string) => icon !== '');
                  setEditedField({ ...editedField, customIcons: iconsArray } as any);
                }}
                helperText="Enter icon names separated by commas (e.g., email, booking-link, spam-folder)"
                InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Grid>
          )}

          {/* HTML Content Editor - Show for content type (but not summary-box) */}
          {editedField.type === 'content' && (editedField as any).areaType !== 'summary-box' && (
            <Grid item xs={12}>
              <Box sx={{ mb: 1 }}>
                <InputLabel sx={{ mb: 1, fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                  Content
                </InputLabel>

                {/* Show HTML editor for custom-icons, ReactQuill for others */}
                {(editedField as any).listStyle === 'custom-icons' ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    value={(editedField as any).content || ''}
                    onChange={(e) => {
                      setEditedField({ ...editedField, content: e.target.value } as any);
                    }}
                    placeholder="Enter HTML content with structure:
<ul>
  <li>
    <strong>Title text</strong>
    <p>Helper text that appears below</p>
  </li>
</ul>"
                    helperText="Write HTML with <strong> for title and <p> for helper text within each <li>"
                  />
                ) : (
                  <Box ref={contentEditorRef} sx={colorTooltipSx}>
                    {contentVariables.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        <Typography variant="caption" sx={{ width: '100%', color: POPUP_COLORS.titleText }}>
                          Insert variable{contentVariableContext === 'LEAD' ? ' (lead)' : ' (patient)'} — empty if not available:
                        </Typography>
                        {contentVariables.map((v) => (
                          <Chip
                            key={v.name}
                            label={v.name}
                            size="small"
                            variant="outlined"
                            onClick={() => insertContentVariable(v.name)}
                            title={v.description}
                          />
                        ))}
                      </Box>
                    )}
                    <ReactQuill
                      ref={contentQuillRef}
                      value={(editedField as any).content || ''}
                      onChange={(content) => {
                        setEditedField({ ...editedField, content } as any);
                      }}
                      modules={contentModules}
                      style={{ height: '250px', marginBottom: '50px' }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          )}

          {/* Summary Box Configuration - Show for content type with areaType='summary-box' */}
          {editedField.type === 'content' && (editedField as any).areaType === 'summary-box' && (
            <Grid item xs={12}>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#3A7380', fontWeight: 600 }}>
                  Summary Box Configuration
                </Typography>

                {/* Header */}
                <TextField
                  fullWidth
                  size="small"
                  label="Header (Optional)"
                  value={(editedField as any).summaryData?.header || ''}
                  onChange={(e) => {
                    const summaryData = (editedField as any).summaryData || { header: '', rows: [] };
                    setEditedField({
                      ...editedField,
                      summaryData: { ...summaryData, header: e.target.value }
                    } as any);
                  }}
                  placeholder="e.g., Assessment Stage"
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />

                {/* Rows */}
                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                  Summary Rows
                </Typography>

                {((editedField as any).summaryData?.rows || []).map((row: any, index: number) => (
                  <Box key={index} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2, backgroundColor: '#fafafa' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          required
                          label="Label"
                          value={row.label || ''}
                          onChange={(e) => {
                            const summaryData = (editedField as any).summaryData || { header: '', rows: [] };
                            const newRows = [...summaryData.rows];
                            newRows[index] = { ...newRows[index], label: e.target.value };
                            setEditedField({
                              ...editedField,
                              summaryData: { ...summaryData, rows: newRows }
                            } as any);
                          }}
                          placeholder="e.g., Total"
                          size="small"
                          InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                          InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Value Type</InputLabel>
                          <Select
                            value={row.valueType || 'static'}
                            label="Value Type"
                            sx={CLINIC_DIALOG_SX.select}
                            onChange={(e) => {
                              const summaryData = (editedField as any).summaryData || { header: '', rows: [] };
                              const newRows = [...summaryData.rows];
                              newRows[index] = { ...newRows[index], valueType: e.target.value };
                              setEditedField({
                                ...editedField,
                                summaryData: { ...summaryData, rows: newRows }
                              } as any);
                            }}
                          >
                            <MenuItem value="static">Static Text</MenuItem>
                            <MenuItem value="displayAmount">Display Amount (£)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        {row.valueType !== 'displayAmount' && (
                          <TextField
                            fullWidth
                            required
                            label="Value"
                            value={row.value || ''}
                            onChange={(e) => {
                              const summaryData = (editedField as any).summaryData || { header: '', rows: [] };
                              const newRows = [...summaryData.rows];
                              newRows[index] = { ...newRows[index], value: e.target.value };
                              setEditedField({
                                ...editedField,
                                summaryData: { ...summaryData, rows: newRows }
                              } as any);
                            }}
                            placeholder="e.g., £1000"
                            size="small"
                            InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                          />
                        )}
                        {row.valueType === 'displayAmount' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', height: '40px', color: '#666', fontSize: '0.875rem' }}>
                            Auto (from payment)
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            const summaryData = (editedField as any).summaryData || { header: '', rows: [] };
                            const newRows = summaryData.rows.filter((_: any, i: number) => i !== index);
                            setEditedField({
                              ...editedField,
                              summaryData: { ...summaryData, rows: newRows }
                            } as any);
                          }}
                        >
                          Remove Row
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                {/* Add Row Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const summaryData = (editedField as any).summaryData || { header: '', rows: [] };
                    setEditedField({
                      ...editedField,
                      summaryData: {
                        ...summaryData,
                        rows: [...summaryData.rows, { label: '', value: '', valueType: 'static' }]
                      }
                    } as any);
                  }}
                  sx={{ mt: 1 }}
                >
                  + Add Row
                </Button>
              </Box>
            </Grid>
          )}

          {/* Banner Title - Show for info-banner type */}
          {editedField.type === 'info-banner' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                required
                label="Banner Title"
                value={(editedField as any).scoreTitle || ''}
                onChange={(e) =>
                  setEditedField({ ...editedField, scoreTitle: e.target.value } as any)
                }
                variant="outlined"
                placeholder="e.g., Estimated likelihood of ADHD"
                InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Grid>
          )}

          {/* Banner Text - Show for info-banner type */}
          {editedField.type === 'info-banner' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                required
                label="Banner Text"
                value={(editedField as any).scoreNameToDisplay || ''}
                onChange={(e) =>
                  setEditedField({ ...editedField, scoreNameToDisplay: e.target.value } as any)
                }
                variant="outlined"
                placeholder="e.g., High, Medium, Low"
                InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Grid>
          )}

          {/* Placeholder */}
          {['text', 'email', 'textarea', 'date'].includes(editedField.type) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Placeholder (Optional)"
                value={editedField.placeholder || ''}
                onChange={(e) =>
                  setEditedField({ ...editedField, placeholder: e.target.value })
                }
                variant="outlined"
                InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Grid>
          )}

          {/* Helper Text - Hide for file, content, info-banner, button-group, line, container, and checkbox types */}
          {!['file', 'content', 'info-banner', 'button-group', 'line', 'container', 'checkbox'].includes(editedField.type) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Helper Text (Optional)"
                value={editedField.helperText || ''}
                onChange={(e) =>
                  setEditedField({ ...editedField, helperText: e.target.value })
                }
                multiline
                rows={2}
                variant="outlined"
                InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Grid>
          )}

          {/* Required - Hide for button, content, info-banner, line, and container types */}
          {!['button', 'content', 'info-banner', 'line', 'container'].includes(editedField.type) && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editedField.required || false}
                    onChange={(e) =>
                      setEditedField({
                        ...editedField,
                        required: e.target.checked,
                      })
                    }
                  />
                }
                label="Required field"
              />
            </Grid>
          )}

          {/* Email verification - Show for email type */}
          {editedField.type === 'email' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editedField.validateEmail || false}
                    onChange={(e) =>
                      setEditedField({
                        ...editedField,
                        validateEmail: e.target.checked,
                        ...(e.target.checked ? {} : { emailVerificationTemplateId: undefined }),
                      })
                    }
                  />
                }
                label="Validate email (require a 6-digit code sent to the entered address)"
              />
            </Grid>
          )}

          {/* Verification email template - Show when validateEmail is enabled */}
          {editedField.type === 'email' && editedField.validateEmail && (
            <Grid item xs={12}>
              <FormControl
                fullWidth
                size="small"
                error={!editedField.emailVerificationTemplateId}
              >
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Verification Email Template</InputLabel>
                <Select
                  value={editedField.emailVerificationTemplateId ?? ''}
                  label="Verification Email Template"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) =>
                    setEditedField({
                      ...editedField,
                      emailVerificationTemplateId: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                >
                  <MenuItem value="">
                    <em>Select a template…</em>
                  </MenuItem>
                  {emailTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" sx={{ mt: 0.5, color: '#5A7380' }}>
                  {!questionnaireTypeId
                    ? 'Save the questionnaire first, then create an email template for it and select it here.'
                    : emailTemplates.length === 0
                    ? "No email templates exist for this questionnaire. Create one under Email Templates using this questionnaire's context."
                    : <>The template must include the <code>{'{email_validation_code}'}</code> variable, which is replaced with the generated code.</>}
                </Typography>
              </FormControl>
            </Grid>
          )}

          {/* Action - Show for button type */}
          {editedField.type === 'button' && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Action</InputLabel>
                <Select
                  value={editedField.action || 'next'}
                  label="Action"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) => {
                    const newAction = e.target.value as 'next' | 'next-and-score' | 'submit' | 'exit' | 'open-popup';
                    const updatedField: any = { ...editedField, action: newAction };
                    // Clear popupId when switching away from open-popup
                    if (newAction !== 'open-popup') {
                      delete updatedField.popupId;
                    }
                    setEditedField(updatedField);
                  }}
                >
                  <MenuItem value="next">Next</MenuItem>
                  <MenuItem value="next-and-score">Next and Score</MenuItem>
                  <MenuItem value="submit">Submit</MenuItem>
                  <MenuItem value="exit">Exit</MenuItem>
                  <MenuItem value="open-popup">Open Popup</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Exit URL - Show for button type with exit action */}
          {editedField.type === 'button' && editedField.action === 'exit' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Exit URL"
                value={editedField.exitUrl || ''}
                onChange={(e) =>
                  setEditedField({ ...editedField, exitUrl: e.target.value })
                }
                variant="outlined"
                placeholder="https://example.com"
                helperText="URL to redirect to when user clicks exit button"
                InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Grid>
          )}

          {/* Score Name - Show for button type with next-and-score action */}
          {editedField.type === 'button' && editedField.action === 'next-and-score' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                required
                label="Score Name"
                value={editedField.scoreName || ''}
                onChange={(e) =>
                  setEditedField({ ...editedField, scoreName: e.target.value })
                }
                variant="outlined"
                placeholder="e.g., ASRS, PHQ-9, GAD-7"
                helperText="Score name for calculation. The score will be calculated when the button is clicked."
                InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                error={editedField.type === 'button' && editedField.action === 'next-and-score' && !editedField.scoreName?.trim()}
              />
            </Grid>
          )}

          {/* Popup Selection - Show for button type with next-and-score action (optional) */}
          {editedField.type === 'button' && editedField.action === 'next-and-score' && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Select Popup (Optional)</InputLabel>
                <Select
                  value={editedField.popupId || ''}
                  label="Select Popup (Optional)"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) =>
                    setEditedField({ ...editedField, popupId: e.target.value || undefined })
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {availablePopups.map((popup) => (
                    <MenuItem key={popup.id} value={popup.id}>
                      {popup.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Popup Selection - Show for button type with open-popup action */}
          {editedField.type === 'button' && editedField.action === 'open-popup' && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Select Popup</InputLabel>
                <Select
                  value={editedField.popupId || ''}
                  label="Select Popup"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) =>
                    setEditedField({ ...editedField, popupId: e.target.value || undefined })
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {availablePopups.map((popup) => (
                    <MenuItem key={popup.id} value={popup.id}>
                      {popup.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Button Height - Show for button-group type */}
          {editedField.type === 'button-group' && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Button Height</InputLabel>
                <Select
                  value={editedField.buttonHeight || 'medium'}
                  label="Button Height"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) =>
                    setEditedField({ ...editedField, buttonHeight: e.target.value as 'small' | 'medium' | 'large' })
                  }
                >
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="large">Large</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Options Layout - Show for button-group type. Vertical stacks
              full-width buttons so long, sentence-length labels can wrap. */}
          {editedField.type === 'button-group' && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: POPUP_COLORS.titleText }}>Options Layout</InputLabel>
                <Select
                  value={editedField.optionsLayout || 'horizontal'}
                  label="Options Layout"
                  sx={CLINIC_DIALOG_SX.select}
                  onChange={(e) =>
                    setEditedField({
                      ...editedField,
                      optionsLayout: e.target.value as 'horizontal' | 'vertical',
                    })
                  }
                >
                  <MenuItem value="horizontal">Horizontal (side by side)</MenuItem>
                  <MenuItem value="vertical">Vertical (stacked, wraps long labels)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Show/Hide Logic Editor - Hide for file, info-banner, and checkbox types */}
          {!['file', 'info-banner', 'checkbox'].includes(editedField.type) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2, borderColor: 'rgba(54,106,117,0.2)' }} />
              <FieldConditionalLogicEditor
                rule={editedField.conditionalLogic}
                onChange={(rule) =>
                  setEditedField({
                    ...editedField,
                    conditionalLogic: rule,
                  })
                }
                availableFields={availableFields}
                currentFieldName={editedField.name}
              />
            </Grid>
          )}

          {/* Validation - for text, textarea, date, and number fields (exclude file, checkbox, checkboxes, select, multiselect, radio, button, content, info-banner, button-group, line, and container) */}
          {!['file', 'checkbox', 'checkboxes', 'select', 'multiselect', 'radio', 'button', 'content', 'info-banner', 'button-group', 'line', 'container'].includes(editedField.type) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1, borderColor: 'rgba(54,106,117,0.2)' }} />
              <ValidationEditor
                fieldType={editedField.type}
                validation={editedField.validation}
                onChange={handleValidationChange}
                allFields={availableFields}
              />
            </Grid>
          )}

          {/* File Configuration - Show for file type */}
          {editedField.type === 'file' && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1, borderColor: 'rgba(54,106,117,0.2)' }} />
              <Box sx={{ mt: 2 }}>
                <span style={{ fontWeight: 500 }}>File Configuration</span>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* File Type Dropdown */}
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ color: POPUP_COLORS.titleText }}>File Type</InputLabel>
                      <Select
                        value={selectedFileTypeId || ''}
                        label="File Type"
                        sx={CLINIC_DIALOG_SX.select}
                        onChange={(e) => setSelectedFileTypeId(e.target.value as number || undefined)}
                        disabled={loadingFileTypes}
                      >
                        <MenuItem value="">
                          <em>None (no file type)</em>
                        </MenuItem>
                        {fileTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Allow Multiple Files Checkbox */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allowMultiple}
                          onChange={(e) => setAllowMultiple(e.target.checked)}
                        />
                      }
                      label="Allow multiple files"
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: '#F9FAFB',
                    borderRadius: 1,
                    border: '1px solid #E5F0F1',
                  }}
                >
                  <span style={{ fontSize: '0.875rem', color: '#5A7380', display: 'block', marginBottom: '4px' }}>
                    <strong>File Limits:</strong>
                  </span>
                  <span style={{ fontSize: '0.875rem', color: '#5A7380', display: 'block' }}>
                    • Maximum files: {allowMultiple ? '10' : '1'}
                  </span>
                  <span style={{ fontSize: '0.875rem', color: '#5A7380', display: 'block' }}>
                    • Maximum size per file: 10MB
                  </span>
                  <span style={{ fontSize: '0.875rem', color: '#5A7380', display: 'block' }}>
                    • Allowed formats: PDF, JPG, PNG, WebP
                  </span>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Options */}
          {hasOptions && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1, borderColor: 'rgba(54,106,117,0.2)' }} />
              <Box sx={{ mt: 2 }}>
                <span style={{ fontWeight: 500 }}>Options</span>
                {editedField.type === 'button-group' && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: '#E3F2FD',
                      borderRadius: 1,
                      border: '1px solid #90CAF9',
                    }}
                  >
                    <span style={{ fontSize: '0.875rem', color: '#1565C0' }}>
                      ℹ️ You can optionally add scores to options for scoring calculations.
                    </span>
                  </Box>
                )}

                {/* Add Option */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    label="Label"
                    value={optionLabel}
                    onChange={(e) => setOptionLabel(e.target.value)}
                    sx={{ flex: 1 }}
                    InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                  />
                  <TextField
                    size="small"
                    label="Value"
                    value={optionValue}
                    onChange={(e) => setOptionValue(e.target.value)}
                    sx={{ flex: 1 }}
                    InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                  />
                  {editedField.type === 'button-group' && (
                    <TextField
                      size="small"
                      label="Score (Optional)"
                      type="number"
                      value={optionScore}
                      onChange={(e) => setOptionScore(e.target.value)}
                      sx={{ width: '120px' }}
                      placeholder="0"
                      InputLabelProps={{ sx: { color: POPUP_COLORS.titleText } }}
                      InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                    />
                  )}
                  <Button onClick={handleAddOption} variant="outlined" size="small">
                    Add
                  </Button>
                </Box>

                {/* List Options */}
                {options.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {/* Headers */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 1,
                        pb: 1,
                        borderBottom: '2px solid #E5E7EB',
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9em' }}>Label</span>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9em' }}>Value</span>
                      </Box>
                      {['checkboxes', 'multiselect', 'multi-select'].includes(editedField.type) && (
                        <Box sx={{ width: '90px' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9em' }}>
                            Exclusive
                          </span>
                        </Box>
                      )}
                      {editedField.type === 'button-group' && (
                        <>
                          <Box sx={{ width: '80px' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.9em' }}>
                              Score
                            </span>
                          </Box>
                          <Box sx={{ width: '150px' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.9em' }}>
                              Popup
                            </span>
                          </Box>
                          <Box sx={{ width: '80px' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.9em' }}>
                              Reset
                            </span>
                          </Box>
                        </>
                      )}
                      <Box sx={{ width: '80px' }} />
                    </Box>

                    {/* Options List */}
                    {options.map((option, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          p: 1,
                          mb: 0.5,
                          bgcolor: 'grey.50',
                          borderRadius: 0.5,
                          alignItems: 'center',
                          '&:hover': {
                            bgcolor: '#f0f0f0',
                          },
                        }}
                      >
                        <Box sx={{ flex: 1, color: '#333' }}>
                          {option.label}
                        </Box>
                        <Box sx={{ flex: 1, color: '#333' }}>
                          {option.value}
                        </Box>
                        {['checkboxes', 'multiselect', 'multi-select'].includes(editedField.type) && (
                          <Box sx={{ width: '90px', display: 'flex', justifyContent: 'center' }}>
                            <Checkbox
                              size="small"
                              checked={option.exclusive || false}
                              onChange={(e) => {
                                const newOptions = [...options];
                                if (e.target.checked) {
                                  newOptions[index].exclusive = true;
                                } else {
                                  delete newOptions[index].exclusive;
                                }
                                setOptions(newOptions);
                              }}
                            />
                          </Box>
                        )}
                        {editedField.type === 'button-group' && (
                          <>
                            <Box sx={{ width: '80px' }}>
                              <TextField
                                size="small"
                                type="number"
                                value={option.score ?? ''}
                                onChange={(e) => {
                                  const newOptions = [...options];
                                  const scoreValue = e.target.value;

                                  if (scoreValue === '') {
                                    delete newOptions[index].score;
                                  } else {
                                    const scoreNum = parseInt(scoreValue, 10);
                                    if (!isNaN(scoreNum)) {
                                      newOptions[index].score = scoreNum;
                                    }
                                  }
                                  setOptions(newOptions);
                                }}
                                placeholder="0"
                                sx={{ width: '70px' }}
                                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                              />
                            </Box>
                            <Box sx={{ width: '150px' }}>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={option.popupId || ''}
                                  onChange={(e) => {
                                    const newOptions = [...options];
                                    if (e.target.value) {
                                      newOptions[index].popupId = e.target.value;
                                    } else {
                                      delete newOptions[index].popupId;
                                      delete newOptions[index].resetOnBack;
                                    }
                                    setOptions(newOptions);
                                  }}
                                  displayEmpty
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  <MenuItem value="">None</MenuItem>
                                  {availablePopups.map((popup) => (
                                    <MenuItem key={popup.id} value={popup.id}>
                                      {popup.title}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            <Box sx={{ width: '80px', display: 'flex', justifyContent: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={option.resetOnBack || false}
                                disabled={!option.popupId}
                                onChange={(e) => {
                                  const newOptions = [...options];
                                  newOptions[index].resetOnBack = e.target.checked;
                                  setOptions(newOptions);
                                }}
                              />
                            </Box>
                          </>
                        )}
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveOption(index)}
                          sx={{ whiteSpace: 'nowrap', width: '80px' }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          )}

          {/* Score Group - Show for button-group type only */}
          {editedField.type === 'button-group' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Score Group (Optional)"
                value={editedField.scoreGroup || ''}
                onChange={(e) =>
                  setEditedField({ ...editedField, scoreGroup: e.target.value })
                }
                variant="outlined"
                placeholder="e.g., anxiety, depression"
                helperText="Group name for score calculations. Fields with the same score group will have their scores summed together."
                InputLabelProps={{ shrink: true, sx: { color: POPUP_COLORS.titleText } }}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </ClinicDialog>
  );
};
